<template>
  <BasicAlert
    alertLabel="Les champs avec * sont obligatoires."
    class="form--mandatory"
  />
  <CFAResponsableSection />
  <DiplomeViseSection />
  <OrganisationFormationSection />
  <LieuFormationPrincipalSection />
  <FraisFormation
    :frais-data-par-mode
    :readonly
    :nature-frais="store.lov.NatureFrais"
    :modifie-frais
  />
</template>

<script setup lang="ts">
  import { computed } from 'vue'
  import { storeToRefs } from 'pinia'
  import type { FraisDTO } from '@/features/api/extranet/dto/Demande/shared'
  import type { ModeFrais, NatureFrais } from '@/features/domaine/types/opco'
  import { FraisFormation } from '@/features/interface/demande/shared'
  import type { FraisDataParMode } from '@/features/interface/demande/shared/components/frais/Frais.types'
  import { useDemandeCAPPStore } from '../../useDemandeCAPPStore'
  import CFAResponsableSection from './sections/CFAResponsable/CFAResponsableSection.vue'
  import DiplomeViseSection from './sections/diplomeVise/DiplomeViseSection.vue'
  import LieuFormationPrincipalSection from './sections/lieuFormationPrincipal/LieuFormationPrincipalSection.vue'
  import OrganisationFormationSection from './sections/organisationFormation/OrganisationFormationSection.vue'

  import { HelpFraisAnnexe, HelpFraisPedagogique } from '@/features/domaine/types/opco/Demande/CAPP'

  const store = useDemandeCAPPStore()

  const { model, readonly, meta } = storeToRefs(store)
  const { setupCustomUpdater } = store

  const fraisDataParMode = computed<FraisDataParMode>(() => {
    return {
      Pedagogique: {
        frais: model.value?.formation?.fraisFactureCfa ? [model.value.formation.fraisFactureCfa] : [],
        metaFrais: meta.value?.formation?.fraisFactureCfa ? [meta.value?.formation?.fraisFactureCfa] : [],
        fraisTitre: 'Frais facturés par le CFA',
        fraisField: 'fraisFactureCfa',
        fraisHelp: HelpFraisPedagogique,
      },
      Annexe: {
        frais: model.value?.formation?.fraisSupportesCfa ?? [],
        metaFrais: meta.value?.formation?.fraisSupportesCfa,
        fraisTitre: 'Frais supportés par le CFA',
        fraisField: 'fraisSupportesCfa',
        fraisHelp: HelpFraisAnnexe,
        fraisTitreHelp: 'Les frais annexes (repas, hébergement et premier équipement) doivent être inclus dans votre demande de prise en charge afin de pouvoir bénéficier du financement par AKTO.',
      },
    }
  })

  const modifieFrais = setupCustomUpdater<{
    mode: ModeFrais
    nature: NatureFrais
    field: keyof FraisDTO
    valeur?: string | number | null
  }>((patch, { mode, nature, field, valeur }) => {
    const formationPatch = patch.formation!

    const fraisField = fraisDataParMode.value[mode]?.fraisField as 'fraisFactureCfa' | 'fraisSupportesCfa'

    const listeFrais = formationPatch[fraisField]

    let fraisAModifier: FraisDTO

    if (Array.isArray(listeFrais)) {
      fraisAModifier = listeFrais.find((f) => f.nature === nature)!
    } else if (listeFrais?.nature === nature) {
      fraisAModifier = listeFrais
    } else {
      throw new Error(`Le type de données pour ${fraisField} est incompatible`)
    }

    fraisAModifier[field] = valeur as any
  })
</script>
